import { create } from 'zustand'
import type { AgentProgressEvent } from '../services/agentProtocol'
import type { ContextBundle } from '../services/api'
import type { CanonicalEvidenceCitation } from '@aide/platform-types'

export interface StoredMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: number
  response_format?: string
  tool_calls?: { id: string; function: { name: string; arguments: string } }[]
  activityEvents?: AgentProgressEvent[]
  contextBundle?: ContextBundle | null
  citations?: CanonicalEvidenceCitation[]
}

export interface Conversation {
  id: string
  title: string
  projectId: string
  sessionId?: string
  messages: StoredMessage[]
  createdAt: number
  updatedAt: number
}

export type PersonaId = 'product_owner' | 'operations' | 'architect'

export interface Project {
  id: string
  name: string
  description: string
  persona?: PersonaId
  linkedRepositories?: string[]
  createdAt: number
  updatedAt: number
}

export interface UserPreferences {
  defaultPersona: PersonaId
  onboardingCompleted: boolean
  theme?: 'light' | 'dark' | 'system'
  displayName?: string
}

interface ChatState {
  projects: Project[]
  conversations: Conversation[]
  activeProjectId: string | null
  activeConversationId: string | null
  sidebarExpanded: boolean
  userPreferences: UserPreferences

  // Project actions
  createProject: (name: string, description?: string) => Project
  renameProject: (id: string, name: string) => void
  deleteProject: (id: string) => void

  // Conversation actions
  createConversation: (projectId: string, title?: string) => Conversation
  setActiveConversation: (conversationId: string | null) => void
  deleteConversation: (id: string) => void
  renameConversation: (id: string, title: string) => void

  // Message actions
  addMessage: (conversationId: string, message: StoredMessage) => void
  updateConversationSession: (conversationId: string, sessionId: string) => void

  // Persona
  setProjectPersona: (projectId: string, persona: PersonaId) => void
  setProjectRepositories: (projectId: string, repos: string[]) => void

  // UI
  setSidebarExpanded: (expanded: boolean) => void
  setActiveProjectId: (id: string | null) => void

  // User preferences
  setUserPreferences: (prefs: Partial<UserPreferences>) => void
  completeOnboarding: () => void

  // Derived
  getActiveConversation: () => Conversation | undefined
  getProjectConversations: (projectId: string) => Conversation[]
}

const DEFAULT_PROJECT_ID = 'default'
const ONBOARDING_COMPLETED_KEY = 'contextful-onboarding:v1'

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function deriveTitle(content: string): string {
  const cleaned = content.replace(/\n/g, ' ').trim()
  if (cleaned.length <= 50) return cleaned
  return cleaned.slice(0, 47) + '...'
}

type SavedChatState = Pick<ChatState, 'projects' | 'conversations' | 'activeProjectId' | 'activeConversationId' | 'sidebarExpanded' | 'userPreferences'>

function emptyChatState(): SavedChatState {
  const now = Date.now()
  return {
    projects: [{ id: DEFAULT_PROJECT_ID, name: 'General', description: 'Default project for conversations', createdAt: now, updatedAt: now }],
    conversations: [], activeProjectId: DEFAULT_PROJECT_ID, activeConversationId: null,
    sidebarExpanded: true, userPreferences: { defaultPersona: 'product_owner', onboardingCompleted: false },
  }
}

export const useChatStore = create<ChatState>()((set, get) => ({
      ...emptyChatState(),

      createProject: (name, description = '') => {
        const project: Project = {
          id: generateId(),
          name,
          description,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        }
        set(state => ({
          projects: [...state.projects, project],
          activeProjectId: project.id,
        }))
        return project
      },

      renameProject: (id, name) => {
        set(state => ({
          projects: state.projects.map(p =>
            p.id === id ? { ...p, name, updatedAt: Date.now() } : p
          ),
        }))
      },

      deleteProject: (id) => {
        if (id === DEFAULT_PROJECT_ID) return
        set(state => ({
          projects: state.projects.filter(p => p.id !== id),
          conversations: state.conversations.filter(c => c.projectId !== id),
          activeProjectId:
            state.activeProjectId === id ? DEFAULT_PROJECT_ID : state.activeProjectId,
          activeConversationId:
            state.conversations.find(c => c.id === state.activeConversationId)?.projectId === id
              ? null
              : state.activeConversationId,
        }))
      },

      createConversation: (projectId, title) => {
        const conversation: Conversation = {
          id: generateId(),
          title: title || 'New conversation',
          projectId,
          messages: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
        }
        set(state => ({
          conversations: [conversation, ...state.conversations],
          activeConversationId: conversation.id,
          activeProjectId: projectId,
        }))
        return conversation
      },

      setActiveConversation: (conversationId) => {
        const conversation = conversationId
          ? get().conversations.find(c => c.id === conversationId)
          : undefined
        set({
          activeConversationId: conversationId,
          activeProjectId: conversation?.projectId ?? get().activeProjectId,
        })
      },

      deleteConversation: (id) => {
        set(state => ({
          conversations: state.conversations.filter(c => c.id !== id),
          activeConversationId:
            state.activeConversationId === id ? null : state.activeConversationId,
        }))
      },

      renameConversation: (id, title) => {
        set(state => ({
          conversations: state.conversations.map(c =>
            c.id === id ? { ...c, title, updatedAt: Date.now() } : c
          ),
        }))
      },

      addMessage: (conversationId, message) => {
        set(state => {
          const conversations = state.conversations.map(c => {
            if (c.id !== conversationId) return c
            const updated = {
              ...c,
              messages: [...c.messages, message],
              updatedAt: Date.now(),
            }
            if (c.messages.length === 0 && message.role === 'user') {
              updated.title = deriveTitle(message.content)
            }
            return updated
          })
          return { conversations }
        })
      },

      updateConversationSession: (conversationId, sessionId) => {
        set(state => ({
          conversations: state.conversations.map(c =>
            c.id === conversationId ? { ...c, sessionId } : c
          ),
        }))
      },

      setProjectPersona: (projectId, persona) => {
        set(state => ({
          projects: state.projects.map(p =>
            p.id === projectId ? { ...p, persona, updatedAt: Date.now() } : p
          ),
        }))
      },

      setProjectRepositories: (projectId, repos) => {
        set(state => ({
          projects: state.projects.map(p =>
            p.id === projectId ? { ...p, linkedRepositories: repos, updatedAt: Date.now() } : p
          ),
        }))
      },

      setSidebarExpanded: (expanded) => set({ sidebarExpanded: expanded }),

      setUserPreferences: (prefs) => {
        set(state => ({
          userPreferences: { ...state.userPreferences, ...prefs },
        }))
      },

      completeOnboarding: () => {
        set(state => ({
          userPreferences: { ...state.userPreferences, onboardingCompleted: true },
        }))
        try { localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true') }
        catch { /* The session remains usable when browser storage is unavailable. */ }
      },

      setActiveProjectId: (id) => {
        const state = get()
        if (state.activeProjectId === id) return
        const projectConvos = state.conversations
          .filter(c => c.projectId === id)
          .sort((a, b) => b.updatedAt - a.updatedAt)
        set({
          activeProjectId: id,
          activeConversationId: projectConvos[0]?.id ?? null,
        })
      },

      getActiveConversation: () => {
        const { conversations, activeConversationId } = get()
        return conversations.find(c => c.id === activeConversationId)
      },

      getProjectConversations: (projectId) => {
        return get().conversations
          .filter(c => c.projectId === projectId)
          .sort((a, b) => b.updatedAt - a.updatedAt)
      },
}))

const object = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)
const text = (value: unknown): value is string => typeof value === 'string'
const timestamp = (value: unknown) => typeof value === 'number' && Number.isFinite(value)
const persona = (value: unknown) => ['product_owner', 'operations', 'architect'].includes(String(value))

function validSavedState(value: unknown): value is SavedChatState {
  if (!object(value) || !Array.isArray(value.projects) || !Array.isArray(value.conversations)) return false
  if (!value.projects.every(p => object(p) && text(p.id) && text(p.name) && text(p.description)
    && timestamp(p.createdAt) && timestamp(p.updatedAt) && (p.persona === undefined || persona(p.persona))
    && (p.linkedRepositories === undefined || (Array.isArray(p.linkedRepositories) && p.linkedRepositories.every(text))))) return false
  if (!value.conversations.every(c => object(c) && text(c.id) && text(c.title) && text(c.projectId)
    && (c.sessionId === undefined || text(c.sessionId)) && timestamp(c.createdAt) && timestamp(c.updatedAt)
    && Array.isArray(c.messages) && c.messages.every(m => object(m) && text(m.id) && text(m.content)
      && (m.role === 'user' || m.role === 'assistant') && timestamp(m.timestamp)))) return false
  const prefs = value.userPreferences
  return (value.activeProjectId === null || text(value.activeProjectId))
    && (value.activeConversationId === null || text(value.activeConversationId))
    && typeof value.sidebarExpanded === 'boolean' && object(prefs) && persona(prefs.defaultPersona)
    && typeof prefs.onboardingCompleted === 'boolean'
    && (prefs.displayName === undefined || text(prefs.displayName))
    && (prefs.theme === undefined || ['light', 'dark', 'system'].includes(String(prefs.theme)))
}

let boundIdentity: string | null = null
let writableStorageKey: string | null = null
let identityGeneration = 0

export function getChatStoreGeneration(): number { return identityGeneration }

/** Switch synchronously before exposing an authenticated UI. Never claim legacy unscoped history. */
export function bindChatStoreIdentity(tenantId: string | null | undefined, userId: string | null | undefined): void {
  const key = typeof tenantId === 'string' && tenantId.trim() && typeof userId === 'string' && userId.trim()
    ? `contextful-chat-store:v3:${encodeURIComponent(JSON.stringify([tenantId, userId]))}` : null
  if (key && key === boundIdentity) return
  identityGeneration += 1
  boundIdentity = key
  writableStorageKey = null
  let restored = emptyChatState()
  let readable = true
  if (key) {
    try {
      const onboardingCompleted = localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true'
      const raw = localStorage.getItem(key)
      if (raw !== null) {
        const saved: unknown = JSON.parse(raw)
        if (!object(saved) || saved.version !== 3 || !validSavedState(saved.state)) throw new Error('Invalid chat history')
        // Only data fields can be restored; storage cannot replace store actions.
        const state = saved.state
        restored = { projects: state.projects, conversations: state.conversations,
          activeProjectId: state.activeProjectId, activeConversationId: state.activeConversationId,
          sidebarExpanded: state.sidebarExpanded, userPreferences: state.userPreferences }
      }
      if (onboardingCompleted) {
        restored.userPreferences = { ...restored.userPreferences, onboardingCompleted: true }
      }
    } catch {
      // Preserve unreadable history in place and keep this session memory-only.
      readable = false
    }
  }
  useChatStore.setState(restored)
  writableStorageKey = readable ? key : null
}

useChatStore.subscribe(state => {
  if (!writableStorageKey) return
  const saved: SavedChatState = {
    projects: state.projects,
    conversations: state.conversations.map(c => ({ ...c, messages: c.messages.slice(-200) })),
    activeProjectId: state.activeProjectId, activeConversationId: state.activeConversationId,
    sidebarExpanded: state.sidebarExpanded, userPreferences: state.userPreferences,
  }
  try { localStorage.setItem(writableStorageKey, JSON.stringify({ version: 3, state: saved })) }
  catch { /* Storage may be unavailable or full; the current account can continue in memory. */ }
})
